import { useState, useEffect } from 'react'
import { User, AuthState } from '../types'
import {
  hashPassword,
  verifyPassword,
  loginRateLimiter,
  SessionManager,
  authenticateBiometric,
  setLastBiometricUsername,
} from '../utils/security'

const USERS_KEY = 'wallet_users'
const CURRENT_USER_KEY = 'wallet_current_user'

// Initialize admin user if not exists
const initializeAdmin = async () => {
  const users = getUsersFromStorage()
  const adminExists = users.some((u) => u.username === 'MarOne')

  if (!adminExists) {
    const hashedPassword = await hashPassword('Marwanbel159753@')
    const admin: User = {
      username: 'MarOne',
      password: hashedPassword,
      role: 'admin',
      createdAt: new Date().toISOString(),
    }
    users.push(admin)
    localStorage.setItem(USERS_KEY, JSON.stringify(users))
  }
}

const getUsersFromStorage = (): User[] => {
  try {
    const stored = localStorage.getItem(USERS_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

const getCurrentUserFromStorage = (): User | null => {
  try {
    const stored = localStorage.getItem(CURRENT_USER_KEY)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>(() => {
    const currentUser = getCurrentUserFromStorage()
    return {
      currentUser,
      isAuthenticated: currentUser !== null,
    }
  })
  const [sessionManager] = useState(() => new SessionManager())

  useEffect(() => {
    initializeAdmin()
  }, [])

  useEffect(() => {
    if (authState.isAuthenticated) {
      sessionManager.startSession(() => {
        // Session timeout - auto logout
        logout()
        alert('Your session has expired. Please sign in again.')
      })
    } else {
      sessionManager.endSession()
    }

    return () => {
      sessionManager.endSession()
    }
  }, [authState.isAuthenticated])

  const login = async (
    username: string,
    password: string,
  ): Promise<boolean> => {
    // Check rate limiting
    if (loginRateLimiter.isBlocked(username)) {
      const timeRemaining = loginRateLimiter.getBlockedTimeRemaining(username)
      const minutes = Math.ceil(timeRemaining / 60000)
      alert(
        `Too many failed attempts. Please try again in ${minutes} minute(s).`,
      )
      return false
    }

    const users = getUsersFromStorage()

    // Find user and verify password
    for (const user of users) {
      if (user.username === username) {
        const isValid = await verifyPassword(password, user.password)

        if (isValid) {
          localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user))
          setAuthState({
            currentUser: user,
            isAuthenticated: true,
          })
          loginRateLimiter.reset(username)
          setLastBiometricUsername(username)
          return true
        }
      }
    }

    // Record failed attempt
    loginRateLimiter.recordAttempt(username)
    const remaining = loginRateLimiter.getRemainingAttempts(username)

    if (remaining > 0) {
      console.warn(`Failed login attempt. ${remaining} attempts remaining.`)
    }

    return false
  }

  const loginWithBiometric = async (username: string): Promise<boolean> => {
    try {
      const success = await authenticateBiometric(username)

      if (success) {
        const users = getUsersFromStorage()
        const user = users.find((u) => u.username === username)

        if (user) {
          localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user))
          setAuthState({
            currentUser: user,
            isAuthenticated: true,
          })
          loginRateLimiter.reset(username)
          setLastBiometricUsername(username)
          return true
        }
      }

      return false
    } catch (error) {
      console.error('Biometric login failed:', error)
      return false
    }
  }

  const logout = () => {
    localStorage.removeItem(CURRENT_USER_KEY)
    setAuthState({
      currentUser: null,
      isAuthenticated: false,
    })
    sessionManager.endSession()
  }

  const addUser = async (
    username: string,
    password: string,
  ): Promise<boolean> => {
    if (authState.currentUser?.role !== 'admin') return false

    // Sanitize username
    const sanitizedUsername = username.trim()
    if (!sanitizedUsername || sanitizedUsername.length < 3) {
      return false
    }

    const users = getUsersFromStorage()
    const exists = users.some((u) => u.username === sanitizedUsername)

    if (exists) return false

    const hashedPassword = await hashPassword(password)
    const newUser: User = {
      username: sanitizedUsername,
      password: hashedPassword,
      role: 'user',
      createdAt: new Date().toISOString(),
    }

    users.push(newUser)
    localStorage.setItem(USERS_KEY, JSON.stringify(users))
    return true
  }

  const deleteUser = (username: string): boolean => {
    if (authState.currentUser?.role !== 'admin') return false
    if (username === 'MarOne') return false // Can't delete admin

    const users = getUsersFromStorage()
    const filtered = users.filter((u) => u.username !== username)

    if (filtered.length === users.length) return false

    localStorage.setItem(USERS_KEY, JSON.stringify(filtered))

    // Clear user's data
    localStorage.removeItem(`transactions_${username}`)
    localStorage.removeItem(`bills_${username}`)
    localStorage.removeItem(`biometric_${username}`)

    return true
  }

  const getAllUsers = (): User[] => {
    if (authState.currentUser?.role !== 'admin') return []
    return getUsersFromStorage()
  }

  const updatePassword = async (newPassword: string): Promise<boolean> => {
    if (!authState.currentUser) return false

    const hashedPassword = await hashPassword(newPassword)
    const users = getUsersFromStorage()
    const updated = users.map((u) =>
      u.username === authState.currentUser!.username
        ? { ...u, password: hashedPassword }
        : u,
    )

    localStorage.setItem(USERS_KEY, JSON.stringify(updated))

    const updatedUser = { ...authState.currentUser, password: hashedPassword }
    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(updatedUser))

    setAuthState({
      currentUser: updatedUser,
      isAuthenticated: true,
    })

    return true
  }

  return {
    ...authState,
    login,
    loginWithBiometric,
    logout,
    addUser,
    deleteUser,
    getAllUsers,
    updatePassword,
  }
}
